import { useState } from "react";
import { Lock, Sparkles, ArrowRight, Check, Loader2, Mail, Package } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { API, useAuth } from "../App";

const money = (n) =>
  typeof n === "number" ? `$${n.toLocaleString()}` : "—";

// Shown in place of a page whose à-la-carte feature the agency has not purchased.
// Fail-closed at the route level: even a deep link lands here.
export default function AlacarteLockedUpsell({ module, price, canTrial }) {
  const navigate = useNavigate();
  const { isSuperAdmin, isAdmin } = useAuth();
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [trialing, setTrialing] = useState(false);

  const startTrial = async () => {
    setTrialing(true);
    try {
      await axios.post(`${API}/alacarte/start-trial`, { feature_id: module?.id });
      toast.success(`14-day free trial started for ${module?.label}.`);
      try { sessionStorage.removeItem("__hg_alacarte"); } catch (e) { /* ignore */ }
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start trial. Please try again.");
      setTrialing(false);
    }
  };

  const requestAccess = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/alacarte/request-access`, {
        feature_id: module?.id,
        note: note.trim() || undefined,
      });
      setSent(true);
      toast.success("Request sent — our team will reach out shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto mt-8" data-testid="alacarte-locked-upsell">
      <div className="rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-xl">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 px-8 py-10 text-center">
          <div className="absolute inset-0 opacity-20 pointer-events-none">
            <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-64 h-40 rounded-full blur-3xl bg-emerald-500" />
          </div>
          <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 border border-white/15 mb-4">
            <Lock className="w-7 h-7 text-emerald-300" />
          </div>
          <p className="relative text-xs font-bold uppercase tracking-[0.2em] text-emerald-300 mb-2">
            Module not enabled
          </p>
          <h1 className="relative text-2xl font-bold text-white font-manrope" data-testid="upsell-feature-name">
            {module?.label || "This capability"}
          </h1>
          {module?.category && (
            <p className="relative text-sm text-slate-300 mt-2">{module.category}</p>
          )}
          {typeof price === "number" && (
            <div className="relative inline-flex items-baseline gap-1 mt-4 rounded-full bg-white/10 border border-white/15 px-4 py-1.5">
              <span className="text-xl font-bold text-white">{money(price)}</span>
              <span className="text-xs text-slate-300">/ month</span>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="bg-white dark:bg-slate-900 px-8 py-7">
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-5">
            This module isn&apos;t enabled for your agency yet.{" "}
            <span className="font-semibold text-slate-900 dark:text-white">
              {module?.desc}
            </span>
          </p>

          <ul className="space-y-2.5 mb-7">
            {[
              "Add it à la carte — pick exactly the modules your agency needs",
              "Everything runs on the same HIPAA-compliant, audit-tracked base",
              "Full platform caps at $7,000/mo — the more you add, the more you save",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm">
                <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                  <Check className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                </span>
                <span className="text-slate-700 dark:text-slate-300">{item}</span>
              </li>
            ))}
          </ul>

          {isSuperAdmin ? (
            <Button
              onClick={() => navigate("/app/module-provisioning")}
              className="w-full h-12 rounded-xl bg-slate-900 hover:bg-slate-800 text-white gap-2"
              data-testid="upsell-provision-btn"
            >
              <Package className="w-4 h-4" />
              Provision modules &amp; pricing
              <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <div className="space-y-4">
              {canTrial && isAdmin && !sent && (
                <div
                  className="rounded-2xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50/70 dark:bg-emerald-950/30 p-5"
                  data-testid="upsell-trial-card"
                >
                  <p className="text-sm font-semibold text-slate-900 dark:text-white mb-1">Try it free for 14 days</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                    Full access, no charge, no card. It locks again when the trial ends — your data is kept.
                  </p>
                  <Button
                    onClick={startTrial}
                    disabled={trialing}
                    className="w-full h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                    data-testid="upsell-start-trial-btn"
                  >
                    {trialing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    Start 14-day free trial
                  </Button>
                </div>
              )}

              {sent ? (
                <div
                  className="rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900 p-5 text-center"
                  data-testid="upsell-sent"
                >
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/50 mb-2">
                    <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">Request sent</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Our team will reach out about enabling <span className="font-semibold">{module?.label}</span> for your agency.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    {canTrial && isAdmin
                      ? <>Prefer to talk first? Reach out about <span className="font-semibold">{module?.label}</span>.</>
                      : <>Want <span className="font-semibold">{module?.label}</span>? Let&apos;s talk.</>}
                  </p>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Optional: tell us a bit about your needs…"
                    rows={3}
                    className="resize-none"
                    data-testid="upsell-note"
                  />
                  <Button
                    onClick={requestAccess}
                    disabled={sending}
                    className="w-full h-12 rounded-xl bg-slate-900 hover:bg-slate-800 text-white gap-2"
                    data-testid="upsell-contact-sales-btn"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    Contact sales to enable this module
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
